import { createSlice, createAction } from "@reduxjs/toolkit";

// ---------------------------------------------------------------------------
// Legacy action creators (same string types as the original user reducer)
// ---------------------------------------------------------------------------

export const loginUserRequest = createAction("LOGIN_USER_REQUEST");
export const loginUserSuccess = createAction("LOGIN_USER_SUCCESS");
export const loginUserFailed = createAction("LOGIN_USER_FAILED");

export const getUserRequest = createAction("GET_USER_REQUEST");
export const getUserSuccess = createAction("GET_USER_SUCCESS");
export const getUserFailed = createAction("GET_USER_FAILED");

export const registerUserRequest = createAction("REGISTER_USER_REQUEST");
export const registerUserSuccess = createAction("REGISTER_USER_SUCCESS");
export const registerUserFailed = createAction("REGISTER_USER_FAILED");

export const forgotPasswordRequest = createAction("FORGOT_PASSWORD_REQUEST");
export const forgotPasswordSuccess = createAction("FORGOT_PASSWORD_SUCCESS");
export const forgotPasswordFailed = createAction("FORGOT_PASSWORD_FAILED");

export const logoutUser = createAction("LOGOUT_USER");
export const activateFirstModal = createAction("ACTIVATE_FIRST_MODAL");

function pickUserFromLoginPayload(payload) {
  if (!payload) return null;
  if (payload.user != null) return payload.user;
  const { loginToken, loading, isLogin, error, ...rest } = payload;
  return Object.keys(rest).length ? rest : null;
}

const initialState = {
  loading: false,
  loginToken: localStorage.getItem("loginToken") || null,
  user: (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  })(),
  error: null,
  isFirstModalOpen: false,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUserRequest, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUserSuccess, (state, action) => {
        const payload = action.payload;
        const loginToken = payload?.loginToken;
        const user = pickUserFromLoginPayload(payload);
        if (loginToken) localStorage.setItem("loginToken", loginToken);
        if (user) localStorage.setItem("user", JSON.stringify(user));

        state.loading = false;
        if (loginToken != null) state.loginToken = loginToken;
        state.user = user ?? state.user;
        state.error = null;
      })
      .addCase(loginUserFailed, (state, action) => {
        localStorage.removeItem("loginToken");
        localStorage.removeItem("user");
        state.loading = false;
        state.loginToken = null;
        state.error = action.payload?.error ?? "Login failed";
      })

      .addCase(getUserRequest, (state) => {
        state.loading = true;
      })
      .addCase(getUserSuccess, (state, action) => {
        const { user: userData, loginToken: token } = action.payload;
        if (userData) localStorage.setItem("user", JSON.stringify(userData));
        if (token) localStorage.setItem("loginToken", token);

        state.loading = false;
        state.user = userData;
        state.loginToken = token ?? state.loginToken;
      })
      .addCase(getUserFailed, (state, action) => {
        localStorage.removeItem("loginToken");
        localStorage.removeItem("user");

        state.loading = false;
        state.loginToken = null;
        state.user = null;
        state.error = action.payload?.error ?? "Failed to fetch user";
      })

      .addCase(registerUserRequest, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUserSuccess, (state) => {
        state.loading = false;
      })
      .addCase(registerUserFailed, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error ?? "Registration failed";
      })

      .addCase(forgotPasswordRequest, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordSuccess, (state) => {
        state.loading = false;
      })
      .addCase(forgotPasswordFailed, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error ?? "Request failed";
      })

      .addCase(logoutUser, (state) => {
        localStorage.removeItem("loginToken");
        localStorage.removeItem("user");
        state.loginToken = null;
        state.user = null;
        state.error = null;
      })

      .addCase(activateFirstModal, (state, action) => {
        state.isFirstModalOpen = action.payload;
      });
  },
});

export default userSlice.reducer;
